import { useMemo, useState } from 'react'
import { Button, Menu, Text, TextInput, Tooltip } from '@mantine/core'
import {
  IconBolt,
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconSettings,
} from '@tabler/icons-react'
import { FundGroup } from 'app/models/fund-group'
import { FUND_GROUP_COPY } from './fund-group-copy'
import {
  EntitiesById,
  describeGroup,
  entityNamesOf,
  filterGroups,
  shouldShowDropdownSearch,
} from './fund-group-utils'

type Props = {
  groups: FundGroup[]
  entitiesById: EntitiesById
  onApply: (group: FundGroup) => void
  onManage: () => void
}

/**
 * The green "Quick Allocation" split button in the Update Allocations header. Selecting a group
 * appends its entities to the allocation table (the parent owns that); nothing is saved.
 */
export function QuickAllocationDropdown({
  groups,
  entitiesById,
  onApply,
  onManage,
}: Props) {
  const [opened, setOpened] = useState(false)
  const [query, setQuery] = useState('')
  const showSearch = shouldShowDropdownSearch(groups.length)
  const visible = useMemo(
    () => filterGroups(groups, query, entitiesById),
    [groups, query, entitiesById],
  )

  return (
    <Menu
      shadow="md"
      width={440}
      position="bottom-start"
      withinPortal
      opened={opened}
      onChange={o => {
        setOpened(o)
        if (!o) setQuery('')
      }}
    >
      <Menu.Target>
        <Button
          className="quick-allocation-button"
          leftIcon={<IconBolt size={18} />}
          rightIcon={
            opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
          }
          data-testid="quick-allocation-button"
        >
          {FUND_GROUP_COPY.quickAllocation}
        </Button>
      </Menu.Target>
      <Menu.Dropdown className="quick-allocation-dropdown">
        <Menu.Label className="quick-allocation-dropdown-label">
          {FUND_GROUP_COPY.selectGroup}
        </Menu.Label>
        {showSearch && (
          // Stop key events reaching the Menu so typing does not move its focus/selection.
          <div
            className="quick-allocation-dropdown-search"
            onKeyDown={e => e.stopPropagation()}
          >
            <TextInput
              size="sm"
              icon={<IconSearch size={16} />}
              placeholder={FUND_GROUP_COPY.searchGroups}
              value={query}
              onChange={e => setQuery(e.currentTarget.value)}
              autoFocus
            />
          </div>
        )}
        {groups.length === 0 && (
          <Text className="quick-allocation-dropdown-empty">
            {FUND_GROUP_COPY.noGroupsYet}
          </Text>
        )}
        {groups.length > 0 && visible.length === 0 && (
          <Text className="quick-allocation-dropdown-empty">
            {FUND_GROUP_COPY.noResults(query)}
          </Text>
        )}
        {visible.map(group => (
          // Hovering a group lists every member lender by name; the summary line truncates.
          <Tooltip
            key={group.id}
            label={
              <div className="quick-allocation-tooltip">
                <Text className="quick-allocation-tooltip-title">
                  {FUND_GROUP_COPY.tooltipTitle}
                </Text>
                {entityNamesOf(group, entitiesById).map(name => (
                  <Text key={name} className="quick-allocation-tooltip-name">
                    {name}
                  </Text>
                ))}
              </div>
            }
            position="right"
            withArrow
            withinPortal
            openDelay={250}
            multiline
          >
            <Menu.Item
              className="quick-allocation-item"
              onClick={() => onApply(group)}
            >
              <Text className="quick-allocation-item-name">{group.name}</Text>
              <Text className="quick-allocation-item-summary" lineClamp={1}>
                {describeGroup(group, entitiesById)}
              </Text>
            </Menu.Item>
          </Tooltip>
        ))}
        <Menu.Divider />
        <Menu.Item
          className="quick-allocation-manage"
          icon={<IconSettings size={20} />}
          onClick={onManage}
        >
          {FUND_GROUP_COPY.manage}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}
